import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Codes } from '@/constants/codes';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');

interface CaseReportAccidentPersonsListProps {
  persons: any[];
  involvedPersons?: any[];
  involvedVehicles?: any[];
  isReadOnly?: boolean;
  onChange?: (persons: any[]) => void;
  onAdd?: () => void;
  onEdit?: (person: any) => void;
}

export function CaseReportAccidentPersonsList({
  persons,
  involvedPersons = [],
  involvedVehicles = [],
  isReadOnly = false,
  onChange,
  onAdd,
  onEdit,
}: CaseReportAccidentPersonsListProps) {
  const { theme } = useTheme();
  const rootRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(persons?.length || 0);

  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; item: any }>({
    open: false,
    item: null,
  });

  // Watch for new persons added - scroll to last mobile card
  useEffect(() => {
    const newLength = persons?.length || 0;
    if (newLength > prevLengthRef.current) {
      setTimeout(() => {
        if (rootRef.current) {
          scrollToLastMobileCard(rootRef.current);
        }
      }, 0);
    }
    prevLengthRef.current = newLength;
  }, [persons]);

  const addPerson = useCallback(() => {
    onAdd?.();
  }, [onAdd]);

  const editPerson = useCallback((person: any) => {
    onEdit?.(person);
  }, [onEdit]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const idx = prev.indexOf(id);
      if (idx >= 0) {
        return prev.filter((_, i) => i !== idx);
      }
      return [...prev, id];
    });
  }, []);

  const isExpanded = useCallback((id: string): boolean => {
    return expandedIds.includes(id);
  }, [expandedIds]);

  const confirmDelete = useCallback((person: any) => {
    setDeleteConfirm({ open: true, item: person });
  }, []);

  const doDelete = useCallback(() => {
    const item = deleteConfirm.item;
    if (item) {
      onChange?.(persons.filter((p: any) => p.tempid !== item.tempid));
    }
    setDeleteConfirm({ open: false, item: null });
  }, [deleteConfirm.item, persons, onChange]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ open: false, item: null });
  }, []);

  const getYesNoLabel = useCallback((value: any): string => {
    if (value === 1 || value === '1') return 'Yes';
    if (value === 2 || value === '2') return 'No';
    return 'N/A';
  }, []);

  const getPersonName = useCallback((person: any): string => {
    if (person.personName) {
      return person.personName;
    }
    if (person.npsdPerson && involvedPersons) {
      const found = involvedPersons.find((p: any) =>
        p.id === person.npsdPerson ||
        p.id === Number(person.npsdPerson)
      );
      if (found) {
        return found.name || found.fullName ||
          `${found.firstName || ''} ${found.lastName || ''}`.trim() ||
          'Unknown';
      }
    }
    return 'Unknown';
  }, [involvedPersons]);

  const getVehicleName = useCallback((person: any): string => {
    if (person.vehicleName) {
      return person.vehicleName;
    }
    if (person.npsdVehicle && involvedVehicles) {
      const found = involvedVehicles.find((v: any) =>
        v.id === person.npsdVehicle ||
        v.id === Number(person.npsdVehicle)
      );
      if (found) {
        return found.name ||
          `${found.year || ''} ${found.make || ''} ${found.model || ''}`.trim() ||
          '';
      }
    }
    return '';
  }, [involvedVehicles]);

  const getPersonRoles = useCallback((person: any): string => {
    if (person.npsdPerson && involvedPersons) {
      const found = involvedPersons.find((p: any) =>
        p.id === person.npsdPerson ||
        p.id === Number(person.npsdPerson)
      );
      if (found && found.roles && found.roles.length > 0) {
        const codes = Codes.getPersonCodes ? Codes.getPersonCodes() : [];
        const roleNames = found.roles.map((roleCode: number) => {
          const role = codes.find((c: any) => c.code === roleCode);
          return role ? role.value : '';
        }).filter(Boolean);
        return roleNames.length > 0 ? roleNames.join(', ') : '\u2014';
      }
    }
    return '\u2014';
  }, [involvedPersons]);

  const getFlagClasses = useCallback((value: any): string => {
    const classes = ['flag-badge'];
    // eslint-disable-next-line eqeqeq
    if (value == 1) classes.push('flag-yes');
    // eslint-disable-next-line eqeqeq
    if (value == 2) classes.push('flag-no');
    return classes.join(' ');
  }, []);

  return (
    <div className={'case-report-accident-persons-list' + (theme ? ' ' + theme : '')} ref={rootRef}>
      <div className="header">
        <h1>People in Accident</h1>
        {!isReadOnly && (
          <button
            onClick={addPerson}
            className="btn-add"
            type="button"
          >
            <i className="fas fa-plus"></i> PERSON
          </button>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: '20%' }}>Person</th>
              <th style={{ width: '15%' }}>Roles</th>
              <th style={{ width: '15%' }}>Vehicle</th>
              <th style={{ width: '10%' }}>At Fault</th>
              <th style={{ width: '10%' }}>Injured</th>
              <th style={{ width: '10%' }}>Veh Dmg</th>
              {!isReadOnly && <th style={{ width: '20%' }}>Actions</th>}
            </tr>
          </thead>
          {persons.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={isReadOnly ? 6 : 7} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                  No accident persons added
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {persons.map((person) => (
                <tr key={person.tempid}>
                  <td>
                    <div className="person-name">{getPersonName(person)}</div>
                  </td>
                  <td className="roles-cell">{getPersonRoles(person)}</td>
                  <td>{getVehicleName(person) || '\u2014'}</td>
                  <td>
                    <span className={getFlagClasses(person.isAtFault)}>
                      {getYesNoLabel(person.isAtFault)}
                    </span>
                  </td>
                  <td>
                    <span className={getFlagClasses(person.isInjured)}>
                      {getYesNoLabel(person.isInjured)}
                    </span>
                  </td>
                  <td>
                    <span className={getFlagClasses(person.isVehicleDamaged)}>
                      {getYesNoLabel(person.isVehicleDamaged)}
                    </span>
                  </td>
                  {!isReadOnly && (
                    <td className="actions-cell">
                      <button className="btn-edit" onClick={(e) => { e.stopPropagation(); editPerson(person); }}>Edit</button>
                      <button className="btn-delete" onClick={(e) => { e.stopPropagation(); confirmDelete(person); }}>Remove</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="mobile-cards">
        {persons.length === 0 ? (
          <div className="empty-state">
            <p>No accident persons added</p>
          </div>
        ) : (
          persons.map((person) => (
            <div key={'mobile-' + person.tempid} className="card">
              <div className="card-header">
                <span className="card-person-name">{getPersonName(person)}</span>
                <span className="card-vehicle-name">{getVehicleName(person) || '\u2014'}</span>
              </div>

              <div className="card-grid">
                <div className="card-field card-field-full">
                  <div className="card-field-label">Roles</div>
                  <div className="card-field-value">{getPersonRoles(person)}</div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">At Fault</div>
                  <div className="card-field-value">
                    <span className={getFlagClasses(person.isAtFault)}>
                      {getYesNoLabel(person.isAtFault)}
                    </span>
                  </div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Injured</div>
                  <div className="card-field-value">
                    <span className={getFlagClasses(person.isInjured)}>
                      {getYesNoLabel(person.isInjured)}
                    </span>
                  </div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Veh Damage</div>
                  <div className="card-field-value">
                    <span className={getFlagClasses(person.isVehicleDamaged)}>
                      {getYesNoLabel(person.isVehicleDamaged)}
                    </span>
                  </div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Notes</div>
                  <div className="card-field-value multiline-text">{person.injuryNotes || '\u2014'}</div>
                </div>
              </div>

              {!isReadOnly && (
                <div className="card-actions">
                  <button className="btn-action btn-edit" onClick={() => editPerson(person)}>
                    <i className="fa fa-edit"></i> Edit
                  </button>
                  <button className="btn-action btn-delete" onClick={() => confirmDelete(person)}>
                    <i className="fa fa-trash"></i> Remove
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <ConfirmModal
        open={deleteConfirm.open}
        title="Remove Person?"
        messageHtml="Are you sure you want to remove this person from the accident?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmClass="danger"
        theme={theme}
        onConfirm={doDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}

(window as any).CaseReportAccidentPersonsListReact = CaseReportAccidentPersonsList;
